import { ChainID } from '@jdiyield/constants'
import { Contract, PriceList, Stake, Token } from '@jdiyield/interfaces'
import { api } from '@jdiyield/utils'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import { findToken, makeBatchRequest } from '../utils'

export async function getBalancerReward(
  account: string,
  contract: Contract,
  tokens: Token[],
  priceList: PriceList,
  chainID: ChainID
): Promise<Stake[]> {
  try {
    const { data } = await api.get(
      `https://api.balancer.finance/liquidity-mining/v1/liquidity-provider-multitoken/${account}`
    )

    const result = (data?.result?.['liquidity-providers'] as any[])
      .filter(({ chain_id, current_estimate }) => {
        return chain_id === chainID && current_estimate !== 0
      })
      .map(({ token_address, current_estimate }) => {
        const tokenInfo = findToken(tokens, token_address)
        return {
          ...contract,
          contractAddress: token_address,
          logo: tokenInfo?.logo,
          token: {
            logo: tokenInfo?.logo,
            symbol: tokenInfo?.symbol || '???',
            name: tokenInfo?.name || '???',
            amount: new BigNumber(0),
            value: 0,
            decimals: tokenInfo.decimals,
            address: token_address,
            valueUSD: 0,
            usdPerCoin: priceList[tokenInfo.address]?.usd
          },
          worth: 0,
          pool: 0,
          balance: '0',
          lpAddress: token_address,
          reward: current_estimate,
          rewardSymbol: tokenInfo?.symbol,
          rewardUSD: current_estimate * priceList[tokenInfo.address]?.usd
        } as Stake
      })

    return result
  } catch (error) {
    console.log('getBalancerReward', error)
    return []
  }
}

export const calculateBalancerPool = async (
  web3: Web3,
  tokenAddress: string,
  tokens: Token[],
  priceList: PriceList
): Promise<number> => {
  const { BPVault, StablePool } = await import('@jdiyield/abi')
  const contract = new web3.eth.Contract(StablePool as any, tokenAddress)
  const [poolId, vault, totalSupply, lpDecimals] = await makeBatchRequest(web3, [
    contract.methods.getPoolId().call,
    contract.methods.getVault().call,
    contract.methods.totalSupply().call,
    contract.methods.decimals().call
  ])
  const vaultContract = new web3.eth.Contract(BPVault as any, vault)
  const { '0': pairTokens, '1': balances } = await vaultContract.methods.getPoolTokens(poolId).call()

  const totalValue = pairTokens.reduce((sum, token, index) => {
    const tokenInfo = findToken(tokens, token)
    const price = priceList[tokenInfo?.address]?.usd || 0
    const balance = new BigNumber(balances[index]).dividedBy(`1e${tokenInfo.decimals || 18}`).toNumber()

    return sum + balance * price
  }, 0)
  const supply = new BigNumber(totalSupply).dividedBy(`1e${lpDecimals || 18}`).toNumber()
  const usdPerCoin = totalValue / supply

  return usdPerCoin
}
